import { useCallback } from "react";
import { Button, cn } from "@tellescope/ui";
import { Plus, Trash2, ToggleLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  isLeafCondition,
  isAndCondition,
  isOrCondition,
  isNotCondition,
  isElemMatchCondition,
  type VisibilityCondition,
  type LeafCondition,
  type ItemCondition,
  type DataSourceType,
} from "@tellescope/visibility-schema";
import { ConditionRule } from "./ConditionRule";
import { ElemMatchGroup } from "./ElemMatchGroup";
import { createDefaultLeafCondition, type LogicalOperator } from "./useConditionBuilder";

interface ConditionGroupProps {
  condition: VisibilityCondition;
  path: number[];
  onAddLeaf: (path: number[], leaf: LeafCondition) => void;
  onAddGroup: (path: number[], operator: LogicalOperator) => void;
  onUpdateLeaf: (path: number[], updates: Partial<LeafCondition>) => void;
  onRemove: (path: number[]) => void;
  onToggleOperator: (path: number[]) => void;
  onWrapInNot: (path: number[]) => void;
  onUnwrapNot: (path: number[]) => void;
  onAddElemMatchGroup: (path: number[], source: DataSourceType) => void;
  onUpdateElemMatchSource: (path: number[], source: DataSourceType) => void;
  onAddElemMatchCondition: (path: number[]) => void;
  onUpdateElemMatchCondition: (path: number[], conditionIndex: number, updates: Partial<ItemCondition>) => void;
  onRemoveElemMatchCondition: (path: number[], conditionIndex: number) => void;
  depth?: number;
  className?: string;
}

/**
 * Renders a condition group ($and, $or, $not) or a leaf condition.
 * Recursively renders nested conditions.
 */
export const ConditionGroup = ({
  condition,
  path,
  onAddLeaf,
  onAddGroup,
  onUpdateLeaf,
  onRemove,
  onToggleOperator,
  onWrapInNot,
  onUnwrapNot,
  onAddElemMatchGroup,
  onUpdateElemMatchSource,
  onAddElemMatchCondition,
  onUpdateElemMatchCondition,
  onRemoveElemMatchCondition,
  depth = 0,
  className,
}: ConditionGroupProps) => {
  const { t } = useTranslation();
  // Handle adding a new condition to this group
  const handleAddCondition = useCallback(() => {
    onAddLeaf(path, createDefaultLeafCondition());
  }, [path, onAddLeaf]);

  // Handle adding a nested group
  const handleAddGroup = useCallback(
    (operator: LogicalOperator) => {
      onAddGroup(path, operator);
    },
    [path, onAddGroup]
  );

  // Handle $elemMatch group (per-item evaluation)
  if (isElemMatchCondition(condition)) {
    return (
      <ElemMatchGroup
        condition={condition}
        depth={depth}
        onUpdateSource={(source) => onUpdateElemMatchSource(path, source)}
        onAddCondition={() => onAddElemMatchCondition(path)}
        onUpdateCondition={(i, updates) => onUpdateElemMatchCondition(path, i, updates)}
        onRemoveCondition={(i) => onRemoveElemMatchCondition(path, i)}
        onRemove={() => onRemove(path)}
        className={className}
      />
    );
  }

  // If it's a leaf condition, render the rule editor
  if (isLeafCondition(condition)) {
    return (
      <ConditionRule
        condition={condition}
        onChange={(updates) => onUpdateLeaf(path, updates)}
        onRemove={() => onRemove(path)}
        className={className}
      />
    );
  }

  // Handle $not wrapper
  if (isNotCondition(condition)) {
    return (
      <div className={cn("border-l-2 border-orange-500 pl-3", className)}>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-medium text-orange-600 bg-orange-100 px-2 py-0.5 rounded">{t("NOT")}</span>
          <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => onUnwrapNot(path)}>
            {t("Remove NOT")}
          </Button>
          {depth > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive ml-auto"
              onClick={() => onRemove(path)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
        <ConditionGroup
          condition={condition.$not}
          path={[...path, 0]} // $not has only one child, treat as index 0
          onAddLeaf={onAddLeaf}
          onAddGroup={onAddGroup}
          onUpdateLeaf={onUpdateLeaf}
          onRemove={onRemove}
          onToggleOperator={onToggleOperator}
          onWrapInNot={onWrapInNot}
          onUnwrapNot={onUnwrapNot}
          onAddElemMatchGroup={onAddElemMatchGroup}
          onUpdateElemMatchSource={onUpdateElemMatchSource}
          onAddElemMatchCondition={onAddElemMatchCondition}
          onUpdateElemMatchCondition={onUpdateElemMatchCondition}
          onRemoveElemMatchCondition={onRemoveElemMatchCondition}
          depth={depth + 1}
        />
      </div>
    );
  }

  // Handle $and / $or groups
  const isAnd = isAndCondition(condition);
  const isOr = isOrCondition(condition);
  const children = isAnd ? condition.$and : isOr ? condition.$or : [];
  const operator = isAnd ? "$and" : "$or";
  const operatorLabel = isAnd ? "AND" : "OR";
  const borderColor = isAnd ? "border-blue-500" : "border-green-500";
  const labelBg = isAnd ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700";

  return (
    <div className={cn("border-l-2 pl-3", borderColor, className)}>
      {/* Group header */}
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          className={cn(
            "text-xs font-medium px-2 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity",
            labelBg
          )}
          onClick={() => onToggleOperator(path)}
          title={`Click to switch to ${isAnd ? "OR" : "AND"}`}
        >
          {operatorLabel}
        </button>
        <span className="text-xs text-muted-foreground">
          {t("Match {{type}} of the following", { type: isAnd ? t("all") : t("any") })}
        </span>

        {depth > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive ml-auto"
            onClick={() => onRemove(path)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Children conditions */}
      <div className="space-y-2">
        {children.map((child, index) => (
          <ConditionGroup
            key={index}
            condition={child}
            path={[...path, index]}
            onAddLeaf={onAddLeaf}
            onAddGroup={onAddGroup}
            onUpdateLeaf={onUpdateLeaf}
            onRemove={onRemove}
            onToggleOperator={onToggleOperator}
            onWrapInNot={onWrapInNot}
            onUnwrapNot={onUnwrapNot}
            onAddElemMatchGroup={onAddElemMatchGroup}
            onUpdateElemMatchSource={onUpdateElemMatchSource}
            onAddElemMatchCondition={onAddElemMatchCondition}
            onUpdateElemMatchCondition={onUpdateElemMatchCondition}
            onRemoveElemMatchCondition={onRemoveElemMatchCondition}
            depth={depth + 1}
          />
        ))}
      </div>

      {/* Add buttons */}
      <div className="flex items-center gap-2 mt-2">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleAddCondition}>
          <Plus className="h-3 w-3 mr-1" />
          {t("Add condition")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => handleAddGroup(operator === "$and" ? "$or" : "$and")}
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("Add {{op}} group", { op: operator === "$and" ? "OR" : "AND" })}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onWrapInNot(path)}>
          <ToggleLeft className="h-3 w-3 mr-1" />
          {t("Negate")}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => onAddElemMatchGroup(path, "formResponse")}
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("Add match item group")}
        </Button>
      </div>
    </div>
  );
};
